import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { formatCompact } from '../../../impact/data/impactTypes';

import { circleIcon, halfMoonLeftIcon, halfMoonRightIcon, hourglassIcon } from './icons';
import { summaryBarStyles as styles } from './SummaryBar.styles';

export interface ImpactSummary {
  amountDollars: number;
  areaNames: string[];
  ageLabel: string;
  reachChildren: number;
  wealthSavings: number;
}

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

interface SummaryItem {
  key: string;
  icon: string;
  label: string;
  italic?: boolean;
}

/**
 * Persistent summary of the user's declared impact — gift amount, selected
 * areas, target age, and the projected reach + wealth. Scrolls as an
 * infinite ticker rather than a static row.
 */
export function SummaryBar({
  amountDollars,
  areaNames,
  ageLabel,
  reachChildren,
  wealthSavings,
}: ImpactSummary) {
  const baseItems: SummaryItem[] = [
    { key: 'amount', icon: circleIcon, label: usd.format(amountDollars) },
    ...areaNames.map((name) => ({ key: `area-${name}`, icon: halfMoonLeftIcon, label: name, italic: true })),
    { key: 'age', icon: hourglassIcon, label: ageLabel },
    ...(reachChildren > 0
      ? [{ key: 'reach', icon: circleIcon, label: `${formatCompact(reachChildren)} children`, italic: true }]
      : []),
    ...(wealthSavings > 0
      ? [
          {
            key: 'wealth',
            icon: halfMoonRightIcon,
            label: `${formatCompact(wealthSavings, true)} wealth savings`,
            italic: true,
          },
        ]
      : []),
  ];

  // A handful of short items is far narrower than most viewports, so one
  // copy alone would leave a visible gap before the loop restarts (verified
  // live: with only 2 base items — no location selected, so just amount +
  // age — a fixed 4x repeat produced a "half" narrower than a 1440px
  // viewport). Scale the repeat count to the actual item count instead of a
  // fixed number, so a "half" always has around TARGET_HALF_ITEMS items
  // regardless of how few or many are selected.
  const TARGET_HALF_ITEMS = 20;
  const repeats = Math.max(4, Math.ceil(TARGET_HALF_ITEMS / baseItems.length));
  const half = Array.from({ length: repeats }, (_, rep) =>
    baseItems.map((item) => ({ ...item, key: `${item.key}-${rep}` })),
  ).flat();

  return (
    <Box component="footer" sx={styles.root}>
      <Box sx={styles.track}>
        <SummaryItems items={half} />
        <SummaryItems items={half} ariaHidden />
      </Box>
    </Box>
  );
}

function SummaryItems({ items, ariaHidden }: { items: SummaryItem[]; ariaHidden?: boolean }) {
  return (
    <Box sx={styles.itemsGroup} aria-hidden={ariaHidden}>
      {items.map((item) => (
        <Box key={item.key} sx={styles.item}>
          <Box component="img" src={item.icon} alt="" sx={styles.icon} />
          <Typography component="span" sx={item.italic ? styles.valueItalic : styles.value}>
            {item.label}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}
